import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText, Plus, Search, Filter, Clock, CheckCircle, XCircle,
  AlertCircle, Calendar, DollarSign, Award, File, GraduationCap,
  UserX, Edit3, ArrowRight
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';

const RequestsListPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchRequests();
  }, [filterType]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      // Fetch different request types
      const endpoints = {
        leave: '/hr-v2/requests/leave',
        advance: '/hr-v2/requests/advance',
        commission: '/hr-v2/requests/commission',
        certificate: '/hr-v2/requests/certificate',
        clearance: '/hr-v2/requests/clearance',
        data_change: '/hr-v2/requests/data-change',
        document: '/hr-v2/requests/document',
        training: '/hr-v2/requests/training'
      };

      let allRequests = [];

      if (filterType === 'all') {
        // Fetch all types
        const promises = Object.entries(endpoints).map(async ([type, endpoint]) => {
          try {
            const response = await api.get(endpoint);
            return response.data.map(req => ({ ...req, request_type: type }));
          } catch (error) {
            console.error(`Failed to load ${type} requests:`, error);
            return [];
          }
        });
        
        const results = await Promise.all(promises);
        allRequests = results.flat();
      } else {
        // Fetch specific type
        const endpoint = endpoints[filterType];
        if (endpoint) {
          const response = await api.get(endpoint);
          allRequests = response.data.map(req => ({ ...req, request_type: filterType }));
        }
      }

      // Sort by created_at
      allRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setRequests(allRequests);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل الطلبات' : 'Failed to load requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const requestTypes = [
    { value: 'all', labelAr: 'جميع الطلبات', labelEn: 'All Requests', icon: FileText, color: 'text-gray-600' },
    { value: 'leave', labelAr: 'طلبات الإجازة', labelEn: 'Leave Requests', icon: Calendar, color: 'text-blue-600' },
    { value: 'advance', labelAr: 'طلبات السلف', labelEn: 'Advance Requests', icon: DollarSign, color: 'text-green-600' },
    { value: 'commission', labelAr: 'طلبات العمولة', labelEn: 'Commission Requests', icon: Award, color: 'text-yellow-600' },
    { value: 'certificate', labelAr: 'طلبات التعريف', labelEn: 'Certificate Requests', icon: File, color: 'text-purple-600' },
    { value: 'clearance', labelAr: 'طلبات إخلاء الطرف', labelEn: 'Clearance Requests', icon: UserX, color: 'text-red-600' },
    { value: 'data_change', labelAr: 'طلبات تغيير البيانات', labelEn: 'Data Change Requests', icon: Edit3, color: 'text-indigo-600' },
    { value: 'document', labelAr: 'طلبات رفع المستندات', labelEn: 'Document Requests', icon: FileText, color: 'text-cyan-600' },
    { value: 'training', labelAr: 'طلبات التدريب', labelEn: 'Training Requests', icon: GraduationCap, color: 'text-orange-600' }
  ];

  const getRequestTypeInfo = (type) => {
    return requestTypes.find(t => t.value === type) || requestTypes[0];
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { 
        label: i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending',
        className: 'bg-yellow-500 text-white',
        icon: Clock
      },
      approved_level_1: { 
        label: i18n.language === 'ar' ? 'موافق - مستوى 1' : 'Approved L1',
        className: 'bg-blue-500 text-white',
        icon: CheckCircle
      },
      approved_level_2: { 
        label: i18n.language === 'ar' ? 'موافق - مستوى 2' : 'Approved L2',
        className: 'bg-cyan-500 text-white',
        icon: CheckCircle
      },
      approved: { 
        label: i18n.language === 'ar' ? 'موافق' : 'Approved',
        className: 'bg-green-500 text-white',
        icon: CheckCircle
      },
      rejected: { 
        label: i18n.language === 'ar' ? 'مرفوض' : 'Rejected',
        className: 'bg-red-500 text-white',
        icon: XCircle
      },
      cancelled: { 
        label: i18n.language === 'ar' ? 'ملغي' : 'Cancelled',
        className: 'bg-gray-500 text-white',
        icon: XCircle
      }
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className} gap-1.5`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(req => {
    const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
    const matchesSearch = req.request_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         req.employee_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Calculate stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إدارة الطلبات' : 'Requests Management'}
              </h1>
              <p className="text-gray-600 text-lg">
                {i18n.language === 'ar' ? 'نظام شامل لإدارة جميع طلبات الموظفين' : 'Comprehensive system for all employee requests'}
              </p>
            </div>
            <Button onClick={() => navigate('/hr/requests/new')} className="btn-primary gap-2">
              <Plus className="w-5 h-5" />
              {i18n.language === 'ar' ? 'طلب جديد' : 'New Request'}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: i18n.language === 'ar' ? 'إجمالي الطلبات' : 'Total', value: stats.total, color: 'from-blue-500 to-blue-600', icon: FileText },
              { title: i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending', value: stats.pending, color: 'from-yellow-500 to-yellow-600', icon: Clock },
              { title: i18n.language === 'ar' ? 'موافق عليها' : 'Approved', value: stats.approved, color: 'from-green-500 to-green-600', icon: CheckCircle },
              { title: i18n.language === 'ar' ? 'مرفوضة' : 'Rejected', value: stats.rejected, color: 'from-red-500 to-red-600', icon: XCircle }
            ].map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-gray-600 font-medium">{stat.title}</p>
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="shadow-lg">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {requestTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {i18n.language === 'ar' ? type.labelAr : type.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                    <SelectItem value="pending">{i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending'}</SelectItem>
                    <SelectItem value="approved">{i18n.language === 'ar' ? 'موافق' : 'Approved'}</SelectItem>
                    <SelectItem value="rejected">{i18n.language === 'ar' ? 'مرفوض' : 'Rejected'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Requests List */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>{i18n.language === 'ar' ? 'قائمة الطلبات' : 'Requests List'}</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">{i18n.language === 'ar' ? 'لا توجد طلبات' : 'No requests found'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredRequests.map((request) => {
                    const typeInfo = getRequestTypeInfo(request.request_type);
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <div
                        key={request.id}
                        onClick={() => navigate(`/hr/requests/${request.id}`, { state: { type: request.request_type } })}
                        className="p-4 border rounded-xl hover:shadow-lg transition-all cursor-pointer bg-white"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <div className={`w-10 h-10 rounded-lg ${typeInfo.color} bg-opacity-10 flex items-center justify-center`}>
                                <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900">{request.request_number}</h4>
                                <p className="text-sm text-gray-600">
                                  {i18n.language === 'ar' ? typeInfo.labelAr : typeInfo.labelEn}
                                </p>
                              </div>
                              {getStatusBadge(request.status)}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600 mt-3">
                              <span>{i18n.language === 'ar' ? 'الموظف: ' : 'Employee: '}{request.employee_name}</span>
                              <span>{i18n.language === 'ar' ? 'القسم: ' : 'Department: '}{request.department}</span>
                              <span>
                                {formatDistanceToNow(new Date(request.created_at), { 
                                  addSuffix: true,
                                  locale: i18n.language === 'ar' ? ar : undefined 
                                })}
                              </span>
                            </div>

                            {/* Approval Progress */}
                            {request.approval_workflow && request.approval_workflow.length > 0 && (
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {i18n.language === 'ar' ? 'مسار الموافقة: ' : 'Approval: '}
                                </span>
                                <div className="flex gap-1">
                                  {request.approval_workflow.map((level, idx) => (
                                    <div
                                      key={idx}
                                      className={`w-2 h-2 rounded-full ${
                                        level.status === 'approved' ? 'bg-green-500' :
                                        level.status === 'rejected' ? 'bg-red-500' :
                                        'bg-gray-300'
                                      }`}
                                      title={`${level.approver_name} - ${level.status}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <Button size="sm" variant="outline">
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RequestsListPage;
